import { requestBlob } from '@/utils/http/requestBlob';
import { trackError } from '@/utils/raygun';
import { setImageObjectUrl } from '@/utils/upload/imageObjectUrls';

const DESIGNER_DB_NAME = 'designer';
const DESIGNER_DB_VERSION = 1;
const IMAGE_STORE_NAME = 'images';

interface ImageData {
  buffer: ArrayBuffer;
  type: string;
}

interface AllUserDesigns {
  all: string[];
  draft: string[];
  filter: string[];
}
export type AllUserDesignsGetter = () => AllUserDesigns;
export async function storeImage(
  id: string,
  imageObjectUrl: string,
  getAllUserDesigns: AllUserDesignsGetter
): Promise<void> {
  try {
    const exists = await has(id);
    if (exists) {
      return;
    }

    const blob = await requestBlob(imageObjectUrl);
    const buffer = await new Promise<ArrayBuffer>((resolve) => {
      const fileReader = new FileReader();
      fileReader.readAsArrayBuffer(blob);
      fileReader.onload = () => {
        resolve(fileReader.result as ArrayBuffer);
      };
    });
    const type = blob.type;

    await set(id, { buffer, type });
  } catch (error) {
    if (error instanceof Error) {
      if (error.name === 'SecurityError') {
        return;
      }

      if (error.name === 'QuotaExceededError') {
        // try to make space with increasingly aggressive eviction strategies and try again
        try {
          const designIds = (await keys()) as string[];
          const allUserDesigns = getAllUserDesigns();
          if (
            // delete unknown stored images (e.g. cleared local storage)
            (await deleteImages(getDifference(designIds, allUserDesigns.all))) ||
            // delete stored images not currently used in the draft
            (await deleteImages(getDifference(designIds, allUserDesigns.draft), true)) ||
            // delete stored images not currently filtered
            (await deleteImages(getDifference(designIds, allUserDesigns.filter), true))
          ) {
            // retry again if an image was deleted
            return storeImage(id, imageObjectUrl, getAllUserDesigns);
          }
        } catch (deleteError) {
          return trackError(deleteError);
        }
      }
    }

    trackError(error);
  }
}

function getDifference<T>(minuend: T[], subtrahend: T[]) {
  return minuend.filter((item) => !subtrahend.includes(item));
}

async function deleteImages(ids: string[], removeOne?: boolean) {
  if (ids.length === 0) {
    return false;
  }

  for (const id of ids) {
    await del(id);

    if (removeOne) {
      return true;
    }
  }

  return true;
}

export async function readImage(id: string) {
  try {
    const imageData = await get<ImageData>(id);
    if (imageData) {
      const { buffer, type } = imageData;
      const imageBlob = new Blob([buffer], { type });
      const imageObjectUrl = URL.createObjectURL(imageBlob);
      setImageObjectUrl(id, imageObjectUrl);
      return imageObjectUrl;
    }
  } catch (e) {
    // ignore
  }

  return null;
}

// inspired by idb-keyval@3.2.0
let getIDB: Promise<IDBDatabase>;
function query(type: IDBTransactionMode, callback: (store: IDBObjectStore) => void) {
  if (!getIDB) {
    getIDB = new Promise<IDBDatabase>((resolve, reject) => {
      const openRequest = indexedDB.open(DESIGNER_DB_NAME, DESIGNER_DB_VERSION);
      openRequest.onerror = () => reject(openRequest.error);
      openRequest.onsuccess = () => resolve(openRequest.result);

      openRequest.onupgradeneeded = () => {
        openRequest.result.createObjectStore(IMAGE_STORE_NAME);
      };
    });
  }

  return getIDB.then(
    (db) =>
      new Promise<void>((resolve, reject) => {
        const transaction = db.transaction(IMAGE_STORE_NAME, type);
        transaction.oncomplete = () => resolve();
        transaction.onabort = transaction.onerror = () => reject(transaction.error);
        callback(transaction.objectStore(IMAGE_STORE_NAME));
      })
  );
}

function get<Type>(key: IDBValidKey): Promise<Type> {
  let req: IDBRequest;
  return query('readonly', (store) => {
    req = store.get(key);
  }).then(() => req.result);
}

function set(key: IDBValidKey, value: any): Promise<void> {
  return query('readwrite', (store) => {
    store.put(value, key);
  });
}

function del(key: IDBValidKey): Promise<void> {
  return query('readwrite', (store) => {
    store.delete(key);
  });
}

function keys(): Promise<IDBValidKey[]> {
  const keys: IDBValidKey[] = [];

  return query('readonly', (store) => {
    // This would be store.getAllKeys(), but it isn't supported by Edge or Safari.
    // And openKeyCursor isn't supported by Safari.
    (store.openKeyCursor || store.openCursor).call(store).onsuccess = function () {
      if (!this.result) return;
      keys.push(this.result.key);
      this.result.continue();
    };
  }).then(() => keys);
}

function has(key: IDBValidKey) {
  let req: IDBRequest;
  return query('readonly', (store) => {
    req = store.count(key);
  }).then(() => req.result > 0);
}
